import React, { useMemo, useState } from 'react';
import { ArrowRightIcon, SparklesIcon } from 'lucide-react';
const MAX_PROMPT_CHARS = 4000;
function sanitizePromptText(raw) {
    const doc = new DOMParser().parseFromString(raw, 'text/html');
    return (doc.body.textContent || '').slice(0, MAX_PROMPT_CHARS).trim();
}
/**
 * Lightweight prompt form designed to be embedded in an iframe.
 * No Clerk / auth dependency — on submit it redirects the top-level
 * frame to /forms?p=<prompt> where RequireAuth handles sign-in
 * and the AddFormModal opens with the prompt pre-filled.
 */
export const PromptInitEmbedPage = () => {
    const [prompt, setPrompt] = useState('');
    const [error, setError] = useState(null);
    const [isFocused, setIsFocused] = useState(false);
    const trimmed = useMemo(() => prompt.trim(), [prompt]);
    const onContinue = () => {
        setError(null);
        const text = sanitizePromptText(trimmed);
        if (!text) {
            setError('Please enter a prompt to continue.');
            return;
        }
        const redirectUrl = new URL(window.location.origin + '/forms');
        redirectUrl.searchParams.set('p', text);
        try {
            window.top.location.href = redirectUrl.toString();
        }
        catch {
            window.location.href = redirectUrl.toString();
        }
    };
    return (React.createElement("section", { className: "relative w-full bg-gradient-to-br from-orange-50 via-white to-gray-50 overflow-hidden" },
        React.createElement("div", { className: "w-full relative z-10" },
            React.createElement("div", { className: "bg-white rounded-2xl shadow-2xl border border-gray-200 p-8 relative w-full" },
                isFocused && React.createElement("div", { className: "absolute inset-0 rounded-2xl bg-orange-500/10 blur-xl" }),
                React.createElement("div", { className: "relative z-10" },
                    React.createElement("div", { className: "flex items-center gap-3 mb-4" },
                        React.createElement(SparklesIcon, { className: "text-orange-500", size: 24 }),
                        React.createElement("h3", { className: "text-lg font-semibold text-gray-900" }, "Create your first form with AI")),
                    React.createElement("div", { className: "flex flex-col sm:flex-row gap-3 mb-6" },
                        React.createElement("input", { type: "text", placeholder: "Describe your form or paste a URL, screenshot, or PDF...", value: prompt, onChange: (e) => {
                                const next = e.target.value;
                                if (next.length <= MAX_PROMPT_CHARS)
                                    setPrompt(next);
                            }, onFocus: () => setIsFocused(true), onBlur: () => setIsFocused(false), onKeyDown: (e) => {
                                if (e.key === 'Enter')
                                    onContinue();
                            }, className: "flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-gray-900 transition-all duration-300" }),
                        React.createElement("button", { onClick: onContinue, disabled: !trimmed, className: "bg-orange-500 text-white px-8 py-3 rounded-lg font-medium hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap" },
                            "Generate Form",
                            React.createElement(ArrowRightIcon, { size: 18 }))),
                    error && (React.createElement("div", { className: "mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" }, error)))))));
};
